import { useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api, API, fmtDuration, getVisitorId } from "@/lib/api";
import { PublicShell, PublicError } from "@/components/PublicShell";
import { TrackedPlayer } from "@/components/TrackedPlayer";
import { useWatchTracker } from "@/hooks/useWatchTracker";
import { EndScreen, CtaButton } from "@/components/EndScreen";
import { HubQuestionCard } from "@/components/HubQuestions";
import { ChatWidget } from "@/components/ChatWidget";

export default function Watch() {
  const { slug } = useParams();
  const leadId = localStorage.getItem(`sv_lead_${slug}`);
  const embed = new URLSearchParams(window.location.search).get("embed") === "1";
  const [sess, setSess] = useState(null);
  const [error, setError] = useState(false);
  const probeRef = useRef(null);
  const started = useRef(false);
  const view = useWatchTracker(sess?.session_id, probeRef);
  const [watched, setWatched] = useState(0);
  useEffect(() => {
    if (!view.playing) return;
    const t = setInterval(() => setWatched((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [view.playing]);

  useEffect(() => {
    if (!leadId || started.current) return;
    started.current = true;
    api.post(`/public/campaigns/${slug}/session`, { lead_id: leadId })
      .then((r) => {
        setSess(r.data);
        document.title = r.data.campaign.title;
        api.post(`/public/campaigns/${slug}/visit`, { visitor_id: getVisitorId(), page: "watch" }).catch(() => {});
      })
      .catch((e) => {
        if (e.response?.status === 403) localStorage.removeItem(`sv_lead_${slug}`);
        setError(e.response?.status || true);
      });
  }, [slug, leadId]);

  useEffect(() => {
    if (embed && view.ended) window.parent.postMessage({ type: "sv-asset-done", slug }, window.location.origin);
  }, [embed, view.ended, slug]);

  if (!leadId || error === 403) return <Navigate to={`/c/${slug}`} replace />;
  if (sess?.campaign?.campaign_type === "whitepaper") return <Navigate to={`/c/${slug}/read`} replace />;
  if (error) return <PublicError text="This campaign isn't available." />;
  if (!sess) return <PublicShell><div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-pink-400" /></div></PublicShell>;

  const c = sess.campaign;
  const src = c.video_type === "upload" ? `${API}/public/stream/${c.id}` : c.video_url;
  const pct = view.duration ? Math.min(100, (view.position / view.duration) * 100) : 0;

  return (
    <PublicShell>
      <div className={`max-w-5xl mx-auto px-6 ${embed ? "py-6" : "py-12 lg:py-16"}`}>
        <p className="sv-rise font-mono text-xs uppercase tracking-[0.3em] text-pink-400 mb-4">Now watching</p>
        <h1 data-testid="watch-title" className="sv-rise sv-d1 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-8">{c.title}</h1>
        <div className="sv-rise sv-d2 relative">
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-pink-500/40 via-violet-500/20 to-transparent" />
          <div className="relative"><TrackedPlayer src={src} probeRef={probeRef} /><EndScreen campaign={c} sessionId={sess.session_id} ended={view.ended} /></div>
        </div>
        <div className="sv-rise sv-d3 mt-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between rounded-xl bg-white/[0.03] border border-white/10 backdrop-blur-xl px-5 py-4">
          <div className="flex items-center gap-3">
            <span className={`w-2 h-2 rounded-full ${view.playing ? "bg-pink-500 sv-live" : "bg-slate-600"}`} />
            <span data-testid="watch-duration-tracker-badge" className="font-mono text-sm text-slate-200">
              {fmtDuration(view.position)} / {fmtDuration(view.duration)}
            </span>
          </div>
          <div className="flex-1 sm:max-w-xs h-1 rounded bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-pink-500 to-violet-500 transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
          {!sess.email?.endsWith("@anonymous.invalid") && <span className="text-xs text-slate-500" data-testid="watch-viewer-email">Viewing as {sess.email}</span>}
        </div>
        {view.ended && c.end_cta_url && (
          <div className="mt-5 flex justify-center sv-rise"><CtaButton campaign={c} sessionId={sess.session_id} testid="watch-inline-cta-button" /></div>
        )}
        {(c.headline || c.description) && (
          <div className="sv-rise sv-d4 mt-12 max-w-3xl">
            {c.headline && <h2 className="font-display text-lg text-slate-200 mb-3">{c.headline}</h2>}
            {c.description && <p className="text-sm sm:text-base text-slate-400 leading-relaxed whitespace-pre-line">{c.description}</p>}
          </div>
        )}
      </div>
      <HubQuestionCard slug={slug} leadId={leadId} seconds={watched} />
      <ChatWidget slug={slug} chat={c.chat} />
    </PublicShell>
  );
}
